import React, { useState, useEffect } from 'react'
import axios from 'axios';
import { baseUrl } from "../../../comman/config";
import { useNavigate, useParams, Link } from 'react-router-dom';

// Screen Access: one row per screen this user can reach, one column per operation, a tick where
// they are allowed it.
//
// Department and Role are shown but not editable here -- they belong to the user, and this grid
// only narrows what that pairing already permits. A column the user's ROLE does not hold is not
// offered at all: the grid takes away, it does not give, which is why the sketch shows a Sevarthi
// with ticks under View, Create and Update and nothing under the other four.
function ScreenAccess() {

    const { id } = useParams();
    const navigate = useNavigate();

    const [detail, setDetail] = useState(null);
    const [operations, setOperations] = useState([]);
    const [rows, setRows] = useState([]);
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);

    const role = String(localStorage.getItem('userRole'));
    // The route carries the id the same way every other edit route does.
    const userId = (() => { try { return atob(id); } catch (e) { return id; } })();

    useEffect(() => {
        const getAccess = async () => {
            try {
                const [access, operationList] = await Promise.all([
                    axios.get(`${baseUrl}/user_master/screen_access?user_id=${userId}`),
                    axios.get(`${baseUrl}/role_master/operations`),
                ]);
                const data = access.data?.data;
                setDetail(data);
                setOperations(operationList.data?.data ?? []);
                setRows((data?.screens ?? []).map((screen) => ({
                    ...screen,
                    operations: new Set(screen.operations),
                })));
            } catch (error) {
                console.log(error);
                setSaveError(error?.response?.data?.msg ?? 'Could not load screen access.');
            }
        };
        getAccess();
    }, [userId, role]);

    const toggle = (screenId, operation) => {
        setRows((previous) => previous.map((row) => {
            if (row.screen_id !== screenId) return row;
            const next = new Set(row.operations);
            if (next.has(operation)) {
                next.delete(operation);
            } else {
                next.add(operation);
            }
            return { ...row, operations: next };
        }));
    };

    // Ticking every box in a column, or clearing it. The grid is one row per screen and seven
    // columns; setting a department's worth of screens one box at a time is the sort of thing
    // that gets abandoned half done.
    const toggleColumn = (operation) => {
        const eligible = rows.filter((row) => row.allowed.includes(operation));
        const allOn = eligible.length > 0 && eligible.every((row) => row.operations.has(operation));
        setRows((previous) => previous.map((row) => {
            if (!row.allowed.includes(operation)) return row;
            const next = new Set(row.operations);
            if (allOn) { next.delete(operation); } else { next.add(operation); }
            return { ...row, operations: next };
        }));
    };

    const handleSubmit = async () => {
        setSaveError('');
        setSaving(true);
        try {
            await axios.post(`${baseUrl}/user_master/screen_access`, {
                role,
                user_id: Number(userId),
                screens: rows.map((row) => ({ screen_id: row.screen_id, operations: [...row.operations] })),
            });
            navigate('/user_master/list');
        } catch (error) {
            console.log(error);
            setSaveError(error?.response?.data?.msg ?? 'Could not save screen access.');
        }
        setSaving(false);
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">
                    Screen Access{detail?.user ? `: ${detail.user.username}` : ''}
                </h1>
                <div>
                    <Link to="/user_master/list">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Back</button>
                    </Link>
                </div>
            </div>

            <div className="relative overflow-x-auto shadow-md sm:rounded-lg px-4 py-4">
                <div className="flex flex-wrap gap-8 pb-4">
                    <div>
                        <span className="text-sm font-medium text-gray-900">Department</span>
                        <span className="ms-3 inline-block bg-gray-200 text-gray-700 rounded px-3 py-1 text-sm">
                            {detail?.department_names?.length ? detail.department_names.join(', ') : 'All'}
                        </span>
                    </div>
                    <div>
                        <span className="text-sm font-medium text-gray-900">Role</span>
                        <span className="ms-3 inline-block bg-gray-200 text-gray-700 rounded px-3 py-1 text-sm">
                            {detail?.user?.role_name ?? '-'}
                        </span>
                    </div>
                </div>

                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                        <tr>
                            <th scope="col" className="px-6 py-3">Screen</th>
                            {operations.map((operation) => (
                                <th key={operation.value} scope="col" className="px-3 py-3 text-center">
                                    <button
                                        type="button"
                                        onClick={() => toggleColumn(operation.value)}
                                        className="uppercase hover:underline"
                                        title="Tick or clear this column"
                                    >
                                        {operation.label}
                                    </button>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.screen_id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                <th scope="row" className="px-6 py-3 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                    {row.name}
                                    <span className="ms-2 text-xs font-normal text-gray-500">{row.department_name}</span>
                                </th>
                                {operations.map((operation) => {
                                    const allowed = row.allowed.includes(operation.value);
                                    return (
                                        <td key={operation.value} className="px-3 py-3 text-center">
                                            {allowed ? (
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4"
                                                    checked={row.operations.has(operation.value)}
                                                    onChange={() => toggle(row.screen_id, operation.value)}
                                                    aria-label={`${operation.label} on ${row.name}`}
                                                />
                                            ) : (
                                                // Not a blank cell by accident: the role itself does not hold this
                                                // operation, so there is nothing here to give.
                                                <span className="text-gray-300" title={`The ${detail?.user?.role_name ?? 'role'} role does not hold ${operation.label}`}>&mdash;</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={operations.length + 1} className="px-6 py-6 text-center text-gray-500">
                                    This user's departments and role reach no screens.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {saveError && <div className="text-red-500 mt-4">{saveError}</div>}

                <div className="mt-6 flex items-center gap-x-4">
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saving}
                        className="rounded-md bg-blue-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-600 disabled:opacity-50"
                    >
                        Submit
                    </button>
                    <Link to="/user_master/list">
                        <button type="button" className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900">Cancel</button>
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default ScreenAccess;
